import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { ThemeContext } from 'styled-components';
import avatar from 'assets/images/avatar.png';
import pdfIcon from 'assets/images/pdf-icon.png';
import { status } from 'constants/general';
import { addBulkOrderFormStepsEnum } from 'modules/order/enums';
import orderStrings from 'modules/order/strings';
import SectionDivider from 'modules/common/components/SectionDivider';
import Popup from 'modules/common/components/Popup';
import AutoCompleteIsolated from 'modules/common/components/AutoCompleteIsolated';
import SingleFileUploaderIsolated from 'modules/common/components/SingleFileUploaderIsolated';
import Loader from 'modules/common/components/Loader';

import {
  Wrapper,
  PageTitle,
  AddPromocodeTitle,
  AddPromocodeInput,
  AddPatientBtnWrapper,
  AddPatientBtn,
  ValidationErrMsg,
  OrderItemWrapper,
  OrderItemPatientName,
  OrderItemPatientNameEditIcon,
  OrderItemCount,
  OrderItemAddOrderWrapper,
  OrderItemSearchProductWrapper,
  OrderItemUploadImageBtn,
  OrderItemDeleteIcon,
  BottomBarWrapper,
  OrderSubmitBtnWrapper,
  OrderSubmitBtn,
  OrderBackBtn,
  OrderTotalPrice,
  SubmissionResultWrapper,
  SubmissionResultIconCircle,
  SubmissionResultIcon,
  SubmissionResultText,
  /* ####################### */
  AddressWrapper,
  AddressGrid,
  AddressCard,
  AddressType,
  AddressDetailsWrapper,
  AddressDetailsTitle,
  AddressDetailsValue,
  AddAddressbtn,
  /* ####################### */
  EnvelopItemWrapper,
  EnvelopItemImage,
  EnvelopItemNameWrapper,
  EnvelopItemName,
  EnvelopItemType,
  EnvelopItemPriceWrapper,
  EnvelopItemPrice,
  EnvelopItemStock,
  LoaderWrapper,
  NoDataText,
  /* ####################### */
  OrderDetailsItemWrapper,
  OrderDetailsItemImage,
  OrderDetailsItemNameWrapper,
  OrderDetailsItemName,
  OrderDetailsItemType,
  OrderDetailsItemPrice,
  OrderDetailsItemActionWrapper,
  OrderDetailsItemActionIcon,
  OrderDetailsItemActionQuantity,
  /* ####################### */
  FileItemWrapper,
  FileItemPadding,
  FileImg,
  ResizedFileImg,
  ImgDeleteOverlay,
  ImgDeleteIcon,
  FileItem,
  ImgPreview,
  /* ####################### */
  AddPatientPopUpWrapper,
  AddPatientPopUpTitleWrapper,
  AddPatientPopUpTitle,
  AddPatientPopUpFormWrapper,
  AddPatientPopUpLabel,
  AddPatientPopUpInput,
  AddPatientPopUpBtn,
} from './BulkOrderComponent.style';

const AddPatientPopUp = ({
  themeContext,
  formFieldsValues,
  formFieldsValidation,
  isInEditMode,
  handleTextInputFieldChange,
  handlePopUpConfirmBtnClick,
  handlePopUpCloseBtnClick,
}) => {
  const formSubmitBtnText =
    (isInEditMode && orderStrings.bulkOrder.editPatientPopUpBtn) ||
    orderStrings.bulkOrder.addPatientPopUpBtn;

  return (
    <Popup handleCloseBtnClick={handlePopUpCloseBtnClick}>
      <AddPatientPopUpWrapper>
        <AddPatientPopUpTitleWrapper>
          <AddPatientPopUpTitle>{orderStrings.bulkOrder.addPatientPopUpTitle}</AddPatientPopUpTitle>
        </AddPatientPopUpTitleWrapper>
        <SectionDivider
          gap={16}
          withSeparator
          separatorGap={16}
          borderColor={themeContext.colors.grey004}
        />
        <AddPatientPopUpFormWrapper>
          <AddPatientPopUpLabel>{orderStrings.bulkOrder.addPatientPopUpLabel}</AddPatientPopUpLabel>
          <SectionDivider gap={10} />
          <AddPatientPopUpInput
            value={formFieldsValues.name}
            onChange={event => handleTextInputFieldChange('name', event)}
          />
          {formFieldsValidation.name === false && (
            <ValidationErrMsg>
              {orderStrings.bulkOrder.formFieldErrorText(
                orderStrings.bulkOrder.addPatientPopUpLabel,
              )}
            </ValidationErrMsg>
          )}
          <SectionDivider gap={30} />
          <AddPatientPopUpBtn as="button" onClick={handlePopUpConfirmBtnClick}>
            {formSubmitBtnText}
          </AddPatientPopUpBtn>
        </AddPatientPopUpFormWrapper>
      </AddPatientPopUpWrapper>
    </Popup>
  );
};
AddPatientPopUp.propTypes = {
  themeContext: PropTypes.shape({
    colors: PropTypes.shape({
      grey004: PropTypes.string,
    }),
  }).isRequired,
  formFieldsValues: PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  formFieldsValidation: PropTypes.shape({
    name: PropTypes.bool,
  }).isRequired,
  isInEditMode: PropTypes.bool.isRequired,
  handleTextInputFieldChange: PropTypes.func.isRequired,
  handlePopUpConfirmBtnClick: PropTypes.func.isRequired,
  handlePopUpCloseBtnClick: PropTypes.func.isRequired,
};
/* ############################################################################### */
/* ############################################################################### */
/* ############################################################################### */
const SearchProductsEnvelop = ({
  index,
  searchProductsInfo,
  handleProductItemClick,
  searchProductItemClickFunc,
}) => {
  const renderSearchProductsResults = () => {
    const {
      details: { productShapes: data },
    } = searchProductsInfo;

    return data.map(result => (
      <EnvelopItemWrapper
        key={result.id}
        onClick={() => handleProductItemClick(result, index, searchProductItemClickFunc)}
      >
        <EnvelopItemImage src={result.productShapeIconUrl} />
        <EnvelopItemNameWrapper>
          <EnvelopItemName>{result.productNameEn}</EnvelopItemName>
          <EnvelopItemType>{result.productShapeTypeName}</EnvelopItemType>
        </EnvelopItemNameWrapper>
        <EnvelopItemPriceWrapper>
          <EnvelopItemPrice>{`${result.newPrice} ${result.currencyEn}`}</EnvelopItemPrice>
          {result.stockQuantity === 0 && (
            <EnvelopItemStock>
              {orderStrings.bulkOrder.orderItemSearchProductEnvelopOutOfStockText}
            </EnvelopItemStock>
          )}
        </EnvelopItemPriceWrapper>
      </EnvelopItemWrapper>
    ));
  };

  const renderSearchProductsEnvelop = () => {
    const { status: autocompleteStatus, details } = searchProductsInfo;
    const data = details?.productShapes;

    if (autocompleteStatus === status.SHOULD_CALL_API || autocompleteStatus === status.FETCHING)
      return (
        <LoaderWrapper>
          <Loader width={30} height={30} thickness={5} />
        </LoaderWrapper>
      );

    if (
      autocompleteStatus === status.FAIL ||
      (autocompleteStatus === status.SUCCESS && (!data || data?.length === 0))
    )
      return (
        <LoaderWrapper>
          <NoDataText>
            {orderStrings.bulkOrder.orderItemSearchProductEnvelopNoResultsText}
          </NoDataText>
        </LoaderWrapper>
      );

    return renderSearchProductsResults();
  };

  return renderSearchProductsEnvelop();
};
SearchProductsEnvelop.propTypes = {
  searchProductsInfo: PropTypes.shape({}).isRequired,
  handleProductItemClick: PropTypes.func.isRequired,
  searchProductItemClickFunc: PropTypes.func.isRequired,
};
/* ############################################################################### */
/* ############################################################################### */
/* ############################################################################### */
const OrderDetails = ({
  index,
  detailsIndex,
  orderDetailsItem,
  handleProductItemQuantityIconClick,
}) => (
  <OrderDetailsItemWrapper>
    <OrderDetailsItemImage src={orderDetailsItem.image} />
    <OrderDetailsItemNameWrapper>
      <OrderDetailsItemName>{orderDetailsItem.name}</OrderDetailsItemName>
      <OrderDetailsItemType>{orderDetailsItem.type}</OrderDetailsItemType>
    </OrderDetailsItemNameWrapper>
    <OrderDetailsItemPrice>{orderDetailsItem.price}</OrderDetailsItemPrice>
    <OrderDetailsItemActionWrapper>
      <OrderDetailsItemActionIcon
        icon={(orderDetailsItem.quantity === 1 && '"\\e9ad"') || '"\\e90e"'}
        onClick={() =>
          handleProductItemQuantityIconClick(
            -1,
            orderDetailsItem.quantity - 1,
            index,
            detailsIndex,
            orderDetailsItem.quantity === 1,
          )
        }
      />
      <OrderDetailsItemActionQuantity>{orderDetailsItem.quantity}</OrderDetailsItemActionQuantity>
      <OrderDetailsItemActionIcon
        icon={'"\\e910"'}
        onClick={() =>
          handleProductItemQuantityIconClick(1, orderDetailsItem.quantity + 1, index, detailsIndex)
        }
      />
    </OrderDetailsItemActionWrapper>
  </OrderDetailsItemWrapper>
);
OrderDetails.propTypes = {
  index: PropTypes.number.isRequired,
  detailsIndex: PropTypes.number.isRequired,
  orderDetailsItem: PropTypes.shape({
    image: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.string,
    price: PropTypes.string,
    quantity: PropTypes.number,
  }).isRequired,
  handleProductItemQuantityIconClick: PropTypes.func.isRequired,
};
/* ############################################################################### */
/* ############################################################################### */
/* ############################################################################### */
const OrderFile = ({
  index,
  detailsIndex,
  orderDetailsItem,
  isPreviewFilePopupOpen,
  previewFileUrl,
  handlePreviewImageClick,
  handleDeleteImageIconClick,
}) => {
  const { dataUrl, shouldBeProcessed, isProcessed, dimensions } = orderDetailsItem;
  const { dimenParamToChange, ratio } = dimensions;

  let imgSrc = dataUrl || avatar;
  let fileOpacity = (isProcessed && 0.6) || 0.5;
  let fileBlur = (isProcessed && 3) || 0;
  if (shouldBeProcessed === false) {
    imgSrc = pdfIcon;
    fileOpacity = 1;
    fileBlur = 0;
  }

  return (
    <>
      <FileItemWrapper>
        <FileItem onClick={() => handlePreviewImageClick(true, orderDetailsItem)}>
          <FileItemPadding />
          <FileImg src={imgSrc} opacity={fileOpacity} blur={fileBlur} />
          {isProcessed && (
            <ResizedFileImg src={imgSrc} dimenParamToChange={dimenParamToChange} ratio={ratio} />
          )}
          <ImgDeleteOverlay />
          <ImgDeleteIcon
            onClick={event => handleDeleteImageIconClick(event, index, detailsIndex)}
          />
        </FileItem>
      </FileItemWrapper>
      {isPreviewFilePopupOpen && (
        <Popup handleCloseBtnClick={() => handlePreviewImageClick()}>
          <ImgPreview src={previewFileUrl} />
        </Popup>
      )}
    </>
  );
};
OrderFile.propTypes = {
  index: PropTypes.number.isRequired,
  detailsIndex: PropTypes.number.isRequired,
  orderDetailsItem: PropTypes.shape({
    dataUrl: PropTypes.string,
    shouldBeProcessed: PropTypes.bool,
    isProcessed: PropTypes.bool,
    dimensions: PropTypes.shape({
      dimenParamToChange: PropTypes.string,
      ratio: PropTypes.number,
    }),
  }).isRequired,
  isPreviewFilePopupOpen: PropTypes.bool.isRequired,
  previewFileUrl: PropTypes.string.isRequired,
  handlePreviewImageClick: PropTypes.func.isRequired,
  handleDeleteImageIconClick: PropTypes.func.isRequired,
};
/* ############################################################################### */
/* ############################################################################### */
/* ############################################################################### */
const BulkOrderComponent = ({
  currentStep,
  orders,
  totalPrice,
  formFieldsValues,
  formFieldsValidation,
  selectedAddress,
  isAddPatientPopupOpen,
  isPreviewFilePopupOpen,
  previewFileUrl,
  isInEditMode,
  getSearchProductsResults,
  resetSearchProductsResults,
  handleTextInputFieldChange,
  handleAddPatientBtnClick,
  handlePopUpConfirmBtnClick,
  handlePopUpCloseBtnClick,
  handleEditPatientNameClick,
  handleDeletePatientIconClick,
  handleProductItemClick,
  handleProductItemQuantityIconClick,
  handleUploadImage,
  handlePreviewImageClick,
  handleDeleteImageIconClick,
  handleAddressCardClick,
  handleAddAddressBtnClick,
  handleSubmitBtnClick,
  handleBackBtnClick,
}) => {
  const { getAddressInfo, searchProductsInfo, submitOrderBulkInfo } = useSelector(state => ({
    getAddressInfo: state.order.getAddressInfo,
    searchProductsInfo: state.order.searchProductsInfo,
    submitOrderBulkInfo: state.order.submitOrderBulkInfo,
  }));
  const themeContext = useContext(ThemeContext);

  const successfulOrdersCount = submitOrderBulkInfo.details?.filter(item => item?.key).length;

  const renderOrderDetailsItem = (index, detailsIndex, orderDetailsItem) => {
    if (orderDetailsItem.isFile)
      return (
        <OrderFile
          key={orderDetailsItem.key}
          index={index}
          detailsIndex={detailsIndex}
          orderDetailsItem={orderDetailsItem}
          isPreviewFilePopupOpen={isPreviewFilePopupOpen}
          previewFileUrl={previewFileUrl}
          handlePreviewImageClick={handlePreviewImageClick}
          handleDeleteImageIconClick={handleDeleteImageIconClick}
        />
      );

    return (
      <OrderDetails
        key={orderDetailsItem.key}
        index={index}
        detailsIndex={detailsIndex}
        orderDetailsItem={orderDetailsItem}
        handleProductItemQuantityIconClick={handleProductItemQuantityIconClick}
      />
    );
  };

  const renderAddOrder = () => (
    <>
      <SectionDivider gap={30} />
      <AddPromocodeTitle>{orderStrings.bulkOrder.AddPromocodeTitle}</AddPromocodeTitle>
      <SectionDivider gap={6} />
      <AddPromocodeInput
        value={formFieldsValues.promocode}
        onChange={event => handleTextInputFieldChange('promocode', event)}
      />
      {formFieldsValidation.promocode === false && (
        <ValidationErrMsg>
          {orderStrings.bulkOrder.formFieldErrorText(orderStrings.bulkOrder.AddPromocodeFieldLabel)}
        </ValidationErrMsg>
      )}
      <SectionDivider gap={15} />
      {orders.map((order, index) => {
        const isLastIndex = index === orders.length - 1;

        return (
          <React.Fragment key={order.patientName}>
            <OrderItemWrapper>
              <OrderItemPatientName>
                {order.patientName}
                <OrderItemPatientNameEditIcon
                  onClick={() => handleEditPatientNameClick(order.patientName, index)}
                />
              </OrderItemPatientName>
              <OrderItemCount>
                {`${orderStrings.bulkOrder.orderItemCountText}: ${order.orderDetails.length}`}
              </OrderItemCount>
              <OrderItemAddOrderWrapper>
                <OrderItemSearchProductWrapper>
                  <AutoCompleteIsolated
                    fieldPlaceholderText={orderStrings.bulkOrder.orderItemSearchProductPlaceholder}
                    // eslint-disable-next-line react/no-unstable-nested-components
                    getEnvelopContent={searchProductItemClickFunc => (
                      <SearchProductsEnvelop
                        index={index}
                        searchProductsInfo={searchProductsInfo}
                        handleProductItemClick={handleProductItemClick}
                        searchProductItemClickFunc={searchProductItemClickFunc}
                      />
                    )}
                    getSearchQueryResults={getSearchProductsResults}
                    resetSearchQueryResults={resetSearchProductsResults}
                  />
                </OrderItemSearchProductWrapper>
                <SingleFileUploaderIsolated
                  supportedMimeTypesRegex={/image/i}
                  fileInputAcceptAttrRegex="image/*"
                  indexToPrepend={index}
                  fileData={null}
                  allowMultipleFilesUpload={false}
                  setUploadedData={uploadedFile => handleUploadImage(uploadedFile, index)}
                >
                  <OrderItemUploadImageBtn as="label" htmlFor={`file-uploader-btn-${index}`} />
                </SingleFileUploaderIsolated>
              </OrderItemAddOrderWrapper>
              <OrderItemDeleteIcon onClick={() => handleDeletePatientIconClick(index)} />
            </OrderItemWrapper>
            {order.orderDetails
              .sort((a, b) => {
                if (a.isFile && !b.isFile) return 1;

                if (!a.isFile && b.isFile) return -1;

                return 0;
              })
              .map((orderDetailsItem, detailsIndex) =>
                renderOrderDetailsItem(index, detailsIndex, orderDetailsItem),
              )}
            {isLastIndex === false && <SectionDivider gap={30} />}
          </React.Fragment>
        );
      })}
      <SectionDivider gap={40} />
      <AddPatientBtnWrapper>
        <AddPatientBtn as="button" onClick={handleAddPatientBtnClick}>
          {orderStrings.bulkOrder.addPatientBtn}
        </AddPatientBtn>
      </AddPatientBtnWrapper>
      {formFieldsValidation.orders === false && (
        <>
          <SectionDivider gap={15} />
          <ValidationErrMsg center>
            {orderStrings.bulkOrder.addOrderBulkErrorMessage}
          </ValidationErrMsg>
        </>
      )}
    </>
  );

  const renderSelectAddress = () => {
    const addressList = getAddressInfo.details;

    return (
      <AddressWrapper>
        <AddressGrid>
          {addressList.map(address => (
            <AddressCard
              key={address.key}
              selected={address.key === selectedAddress.key}
              onClick={() => handleAddressCardClick(address)}
            >
              <AddressType>{address.label}</AddressType>
              <SectionDivider
                gap={8}
                withSeparator
                separatorGap={10}
                borderColor={themeContext.colors.grey004}
              />
              <AddressDetailsWrapper>
                <AddressDetailsTitle>
                  {orderStrings.bulkOrder.chooseAddressNameTitle}
                </AddressDetailsTitle>
                <AddressDetailsValue>{address.recipientName}</AddressDetailsValue>
              </AddressDetailsWrapper>
              <SectionDivider gap={10} />
              <AddressDetailsWrapper>
                <AddressDetailsTitle>
                  {orderStrings.bulkOrder.chooseAddressInfoTitle}
                </AddressDetailsTitle>
                <AddressDetailsValue>{`${address.StreetName}, ${address.completeAddress}`}</AddressDetailsValue>
              </AddressDetailsWrapper>
              <SectionDivider gap={10} />
              <AddressDetailsWrapper>
                <AddressDetailsTitle>
                  {orderStrings.bulkOrder.chooseAddressPhoneTitle}
                </AddressDetailsTitle>
                <AddressDetailsValue>{address.recipientNumber}</AddressDetailsValue>
              </AddressDetailsWrapper>
            </AddressCard>
          ))}
        </AddressGrid>
        <SectionDivider gap={20} />
        <AddAddressbtn as="button" onClick={handleAddAddressBtnClick}>
          {orderStrings.bulkOrder.addAddressBtn}
        </AddAddressbtn>
      </AddressWrapper>
    );
  };

  const renderOrderView = () => {
    if (currentStep === addBulkOrderFormStepsEnum.orderDetails) return renderAddOrder();

    return renderSelectAddress();
  };

  const renderSuccessView = () => (
    <>
      <SectionDivider gap={100} />
      <SubmissionResultWrapper>
        <SubmissionResultIconCircle circleColor={themeContext.colors.greenLight}>
          <SubmissionResultIcon icon={'"\\e900"'} backgroundColor={themeContext.colors.greenDark} />
        </SubmissionResultIconCircle>
        <SectionDivider gap={30} />
        <SubmissionResultText>
          {orderStrings.bulkOrder.addOrderBulkSuccessMessage(successfulOrdersCount)}
        </SubmissionResultText>
      </SubmissionResultWrapper>
    </>
  );

  const renderFailView = () => (
    <>
      <SectionDivider gap={100} />
      <SubmissionResultWrapper>
        <SubmissionResultIconCircle circleColor={themeContext.colors.redLight}>
          <SubmissionResultIcon icon={'"\\e91f"'} backgroundColor={themeContext.colors.redDark} />
        </SubmissionResultIconCircle>
        <SectionDivider gap={30} />
        <SubmissionResultText>
          {orderStrings.bulkOrder.addOrderBulkFailMessage}
        </SubmissionResultText>
      </SubmissionResultWrapper>
    </>
  );

  const renderBulkOrderView = () => {
    if (
      submitOrderBulkInfo.status === status.FETCHING ||
      submitOrderBulkInfo.status === status.SHOULD_CALL_API
    )
      return renderOrderView();

    if (
      submitOrderBulkInfo.status === status.FAIL ||
      (submitOrderBulkInfo.status === status.SUCCESS && successfulOrdersCount === 0)
    )
      return renderFailView();

    return renderSuccessView();
  };

  const formSubmitBtn =
    (currentStep === addBulkOrderFormStepsEnum.orderDetails &&
      orderStrings.bulkOrder.orderFormSubmitBtn) ||
    orderStrings.bulkOrder.addressFormSubmitBtn;

  if (getAddressInfo.status === status.SHOULD_CALL_API || getAddressInfo.status === status.FETCHING)
    return (
      <LoaderWrapper fullHeight>
        <Loader />
      </LoaderWrapper>
    );

  return (
    <>
      <Wrapper>
        <PageTitle>{orderStrings.bulkOrder.pageName}</PageTitle>
        <SectionDivider gap={16} />
        {renderBulkOrderView()}
      </Wrapper>
      <BottomBarWrapper>
        {(submitOrderBulkInfo.status === status.SHOULD_CALL_API ||
          submitOrderBulkInfo.status === status.FETCHING) && (
          <OrderSubmitBtnWrapper>
            {(submitOrderBulkInfo.status === status.FETCHING && (
              <Loader width={32} height={32} thickness={5} />
            )) || (
              <OrderSubmitBtn as="button" onClick={handleSubmitBtnClick}>
                {formSubmitBtn}
              </OrderSubmitBtn>
            )}
          </OrderSubmitBtnWrapper>
        )}
        {currentStep === addBulkOrderFormStepsEnum.addressDetails && (
          <OrderBackBtn as="button" onClick={handleBackBtnClick}>
            {orderStrings.bulkOrder.addressFormBackBtn}
          </OrderBackBtn>
        )}
        <OrderTotalPrice>{orderStrings.bulkOrder.orderTotalPrice(totalPrice)}</OrderTotalPrice>
      </BottomBarWrapper>
      {isAddPatientPopupOpen && (
        <AddPatientPopUp
          themeContext={themeContext}
          formFieldsValues={formFieldsValues}
          formFieldsValidation={formFieldsValidation}
          isInEditMode={isInEditMode}
          handleTextInputFieldChange={handleTextInputFieldChange}
          handlePopUpConfirmBtnClick={handlePopUpConfirmBtnClick}
          handlePopUpCloseBtnClick={handlePopUpCloseBtnClick}
        />
      )}
    </>
  );
};

BulkOrderComponent.propTypes = {
  currentStep: PropTypes.number.isRequired,
  orders: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  totalPrice: PropTypes.number.isRequired,
  formFieldsValues: PropTypes.shape({
    promocode: PropTypes.string,
  }).isRequired,
  formFieldsValidation: PropTypes.shape({
    promocode: PropTypes.bool,
    orders: PropTypes.bool,
  }).isRequired,
  selectedAddress: PropTypes.shape({
    key: PropTypes.string,
  }).isRequired,
  isAddPatientPopupOpen: PropTypes.bool.isRequired,
  isPreviewFilePopupOpen: PropTypes.bool.isRequired,
  previewFileUrl: PropTypes.string.isRequired,
  isInEditMode: PropTypes.bool.isRequired,
  getSearchProductsResults: PropTypes.func.isRequired,
  resetSearchProductsResults: PropTypes.func.isRequired,
  handleTextInputFieldChange: PropTypes.func.isRequired,
  handleAddPatientBtnClick: PropTypes.func.isRequired,
  handlePopUpConfirmBtnClick: PropTypes.func.isRequired,
  handlePopUpCloseBtnClick: PropTypes.func.isRequired,
  handleEditPatientNameClick: PropTypes.func.isRequired,
  handleDeletePatientIconClick: PropTypes.func.isRequired,
  handleProductItemClick: PropTypes.func.isRequired,
  handleProductItemQuantityIconClick: PropTypes.func.isRequired,
  handleUploadImage: PropTypes.func.isRequired,
  handlePreviewImageClick: PropTypes.func.isRequired,
  handleDeleteImageIconClick: PropTypes.func.isRequired,
  handleAddressCardClick: PropTypes.func.isRequired,
  handleAddAddressBtnClick: PropTypes.func.isRequired,
  handleSubmitBtnClick: PropTypes.func.isRequired,
  handleBackBtnClick: PropTypes.func.isRequired,
};

export default BulkOrderComponent;
